/**
 * Karta sprzedażowa pozycji oferty — kontrakt i czyste pomocniki.
 * ================================================================
 *
 * Kontrakt odpowiada temu, co zwraca `oferta_publiczna_pokaz` po migracji 664:
 * każda pozycja niesie obok nazwy i kwot klucz katalogowy, pakiet, funkcje
 * i limity z cennika oraz kartę sprzedażową (albo `null`, gdy katalog nie ma
 * nic do powiedzenia — wtedy strona pokazuje jedno uczciwe zdanie, nie pustą
 * ramkę).
 *
 * Wszystko tutaj jest czyste (bez Supabase, bez DOM), bo ten sam kod czyta
 * strona klienta i podgląd „jak widzi klient” w edytorze super-admina —
 * a testy mają mierzyć decyzje, nie widżety.
 */

export type TypPozycji = 'plan' | 'modul' | 'licencja';
export type TrybPozycji = 'monthly' | 'yearly' | 'perpetual' | 'on_premise';

export interface Korzysc {
  tytul: string;
  opis: string;
}

export interface KrokDzialania {
  krok: string;
  opis: string;
}

export interface PytanieOdpowiedz {
  pytanie: string;
  odpowiedz: string;
}

export type DostawcaWideo = 'plik' | 'youtube' | 'vimeo';

/**
 * Element galerii. Świadomie BEZ adresu URL:
 *   • plik z bucketu `katalog-media` opisuje `sciezka` względna
 *     (`<typ>/<klucz>/<uuid>.<ext>`) — adres składa przeglądarka z hosta
 *     projektu, więc ta sama karta działa na produkcji i na gałęzi podglądowej;
 *   • film z YouTube/Vimeo opisuje sam `id_wideo` — ramka na stronie czytanej
 *     anonimowo nie może wskazywać dowolnego hosta.
 */
export interface Medium {
  rodzaj: 'obraz' | 'wideo';
  dostawca: DostawcaWideo;
  sciezka?: string | null;
  id_wideo?: string | null;
  podpis?: string | null;
  /** Miniatura z bucketu (ścieżka względna). Dla YouTube liczona z `id_wideo`. */
  miniatura?: string | null;
  /**
   * Profil branży, dla którego zrobiono zrzut (kategoria z
   * `tenant_industry_profiles.category`, np. `bakery`, `restaurant`,
   * `manufacturing`); `null` = pasuje do każdego klienta. Zrzut piekarni z
   * chlebem w recepturze nie ma trafić do lakierni.
   */
  profil?: string | null;
  /** Język danych na zrzucie (ISO 639-1); `null` = bez tekstu / uniwersalny. */
  jezyk?: string | null;
}

/** Profile branży znane onboardingowi (`tenant_industry_profiles.category`). */
export const PROFILE_BRANZY: { klucz: string; nazwa: string }[] = [
  { klucz: 'bakery', nazwa: 'Piekarnia / cukiernia' },
  { klucz: 'restaurant', nazwa: 'Gastronomia' },
  { klucz: 'food_production', nazwa: 'Produkcja spożywcza' },
  { klucz: 'manufacturing', nazwa: 'Produkcja przemysłowa' },
  { klucz: 'retail', nazwa: 'Handel' },
  { klucz: 'logistics', nazwa: 'Logistyka' },
];

/** Języki zrzutów — te, dla których platforma ma interfejs. */
export const JEZYKI_MEDIOW: { klucz: string; nazwa: string }[] = [
  { klucz: 'pl', nazwa: 'polski' },
  { klucz: 'en', nazwa: 'angielski' },
  { klucz: 'de', nazwa: 'niemiecki' },
  { klucz: 'uk', nazwa: 'ukraiński' },
  { klucz: 'cs', nazwa: 'czeski' },
];

/**
 * Dobór mediów do klienta — to samo, co robi `oferta_media_dla_klienta` w bazie
 * (664), tutaj dla podglądu w edytorze i dla testów:
 *   1. odpadają zrzuty innego profilu (bez profilu = pasują do każdego);
 *   2. jeśli są zrzuty w języku klienta, zostają one i uniwersalne (bez języka);
 *      jeśli nie ma — zostają polskie i uniwersalne (polski to język platformy);
 *   3. zrzuty właściwego profilu idą przed uniwersalnymi.
 */
export function mediaDlaKlienta(
  media: Medium[],
  profil: string | null,
  jezyk: string | null
): Medium[] {
  const profilOk = (m: Medium) => !m.profil || (profil != null && m.profil === profil);
  const kandydaci = (media ?? []).filter(profilOk);
  const jez = (jezyk ?? 'pl').toLowerCase();
  const wJezyku = kandydaci.filter((m) => !m.jezyk || m.jezyk.toLowerCase() === jez);
  const lista =
    wJezyku.some((m) => m.jezyk && m.jezyk.toLowerCase() === jez) || jez === 'pl'
      ? wJezyku
      : kandydaci.filter((m) => !m.jezyk || m.jezyk.toLowerCase() === 'pl');
  return [...lista].sort((a, b) => Number(Boolean(b.profil)) - Number(Boolean(a.profil)));
}

export interface WariantKarty {
  naglowek?: string | null;
  funkcje?: string[] | null;
}

export interface KartaSprzedazowa {
  /** `katalog` — zredagowana karta; `cennik` — minimum złożone z opisów cennika. */
  zrodlo: 'katalog' | 'cennik';
  nazwa_klienta: string | null;
  naglowek: string | null;
  opis: string | null;
  korzysci: Korzysc[];
  funkcje: string[];
  dla_kogo: string | null;
  jak_dziala: KrokDzialania[];
  faq: PytanieOdpowiedz[];
  media: Medium[];
  wariant: WariantKarty | null;
}

export interface PakietPozycji {
  slug: string;
  nazwa: string;
  odznaka: string | null;
}

export interface KluczPozycji {
  plan?: string;
  module_id?: string;
  license_model?: string;
  device_class?: string;
}

export interface PozycjaOfertyDto {
  typ: TypPozycji;
  nazwa: string;
  tryb: TrybPozycji;
  ilosc: number;
  cena_jednostkowa: number | string;
  kwota: number | string;
  cyklicznie: boolean;
  klucz?: KluczPozycji | null;
  pakiet?: PakietPozycji | null;
  funkcje_pakietu?: string[] | null;
  limity_pakietu?: Record<string, unknown> | null;
  karta?: KartaSprzedazowa | null;
}

/** Tryby rozliczenia bez żargonu — klient ma rozpoznać, kiedy i za co płaci. */
export const ETYKIETY_TRYBU: Record<TrybPozycji, string> = {
  monthly: 'miesięcznie',
  yearly: 'rocznie',
  perpetual: 'jednorazowo, na zawsze',
  on_premise: 'jednorazowo, na Twoim serwerze',
};

/**
 * Limity z cennika (`limits` w `module_prices` / `plan_prices` / `mobile_prices`)
 * jako ZDANIA. Klucz bez zdania nie trafia na ekran — surowy klucz JSON nigdy
 * nie jest zdaniem dla klienta. `-1` w cenniku znaczy „bez limitu”.
 *
 * Uwaga na słowa: nie ma tu „tokenów” ani „miejsc floating” — pula asystenta
 * i miejsca współbieżne są nazwane tym, co klient z nich ma.
 */
const ZDANIA_LIMITOW: Record<string, (n: number) => string> = {
  locations: (n) =>
    n < 0
      ? 'Lokalizacje bez limitu'
      : `${n} ${odmiana(n, 'lokalizacja', 'lokalizacje', 'lokalizacji')}`,
  mobile_seats: (n) =>
    n < 0
      ? 'Bez limitu osób pracujących jednocześnie w aplikacji'
      : `${n} ${odmiana(n, 'osoba', 'osoby', 'osób')} pracujących jednocześnie w aplikacji`,
  // `mobile_devices` i `tv_walls` w cenniku to LICZBA LICENCJI, a nie sztuk
  // sprzętu — plan daje prawo sparowania tabletu i telewizora, które klient
  // już ma (`device_licenses`, `generate_license_code()`, migracja 119).
  // Do 03.09.2026 stało tu „N urządzeń w cenie” i właściciel zgłosił to jako
  // nieprawdę na ofercie OF-2026-0011: cztery pozycje, wszystkie oprogramowanie
  // i licencje, a ekran mówił klientowi „sprzęt na hali w cenie”. Sprzęt jest
  // osobnym zakupem (sklep sprzętu, `marketplace_devices`) i osobną pozycją
  // na fakturze, więc zdanie o limicie musi nazywać licencję licencją.
  mobile_devices: (n) =>
    n < 0
      ? 'Licencje na urządzenia współdzielone bez limitu — sprzęt po Twojej stronie'
      : `${n} ${odmiana(n, 'licencja', 'licencje', 'licencji')} na ${
          n === 1 ? 'urządzenie współdzielone' : 'urządzenia współdzielone'
        } — sprzęt po Twojej stronie`,
  tv_walls: (n) =>
    n < 0
      ? 'Licencje na ekrany na hali bez limitu — telewizor po Twojej stronie'
      : n === 0
        ? ''
        : `${n} ${odmiana(n, 'licencja', 'licencje', 'licencji')} na ${
            n === 1 ? 'ekran na hali' : 'ekrany na hali'
          } — telewizor po Twojej stronie`,
  // Jednostką jest AKCJA, a nie „co dziesiąta akcja": kanon
  // `06-WARSTWA-ZUZYCIA-AI.md` §4.1-4.2 mówi „1 AKCJA = CEIL(total_tokens /
  // 10 000)" i „typowe zapytanie to jedna akcja", więc pula N akcji to do N
  // zapytań. Do 04.09.2026 stał tu dzielnik 10 i klient przy
  // `itokens_month = 2000` czytał „ok. 200 rozmów", mając 2 000 akcji —
  // liczba na ofercie była dziesięciokrotnie niższa od tego, co kupił.
  //
  // Drugi błąd tej samej linii: `Math.max(1, …)` obiecywał „ok. 1 rozmowę"
  // przy puli ZERO, czyli półce `free`, na której asystenta nie ma wcale.
  // Zero znaczy „nie wspominamy o tym w ofercie" — tak jak przy `tv_walls`
  // i `max_robotow` niżej.
  itokens_month: (n) =>
    n < 0
      ? 'Praca asystenta bez limitu'
      : n === 0
        ? ''
        : `Praca asystenta w pakiecie: do ${n} ${odmiana(
            n,
            'zapytania',
            'zapytań',
            'zapytań'
          )} miesięcznie`,
  max_kamer: (n) => `Do ${n} ${odmiana(n, 'kamery', 'kamer', 'kamer')}`,
  retencja_dni: (n) => `${n} dni historii obserwacji`,
  retencja_epizodow_dni: (n) => `${n} dni historii nagrań`,
  max_robotow: (n) =>
    n === 0 ? '' : `Do ${n} ${odmiana(n, 'robota', 'robotów', 'robotów')} w rejestrze`,
  godzin_gpu_miesiecznie: (n) => (n === 0 ? '' : `${n} godzin treningu miesięcznie`),
  max_drukarek: (n) => `Do ${n} ${odmiana(n, 'drukarki', 'drukarek', 'drukarek')}`,
  max_serwerow: (n) =>
    `Do ${n} ${odmiana(n, 'serwera wydruku', 'serwerów wydruku', 'serwerów wydruku')}`,
  wlasne_uklady: (n) =>
    `${n} ${odmiana(n, 'własny układ etykiety', 'własne układy etykiet', 'własnych układów etykiet')}`,
  retencja_artefaktow_dni: (n) => `${n} dni historii wydruków`,
  sensors: (n) =>
    n < 0 ? 'Czujniki bez limitu' : `Do ${n} ${odmiana(n, 'czujnika', 'czujników', 'czujników')}`,
  users: (n) =>
    n < 0
      ? 'Użytkownicy bez limitu'
      : `Do ${n} ${odmiana(n, 'użytkownika', 'użytkowników', 'użytkowników')}`,
  // Agent Integracji Receptor 3.0 — licencja PER SERWER (migracja 754). Liczba
  // na ofercie to `ilosc` pozycji (`oferta_pozycja_szczegoly`), a egzekwuje ją
  // `receptor_licencja` przy parowaniu, pulsie i kolejkowaniu zadań: serwer
  // ponad liczbę nie dostaje pracy. Zero nie trafia na ofertę (nie ma czego
  // obiecać), `-1`/brak limitu — wyłącznie nadanie operatorskie.
  serwery_receptora: (n) =>
    n < 0
      ? 'Serwery Receptora bez limitu'
      : n === 0
        ? ''
        : `Do ${n} ${odmiana(n, 'serwera', 'serwerów', 'serwerów')} Receptora z agentem integracji`,
};

/** Klucze limitów świadomie pominięte na ekranie klienta (nie są jego sprawą). */
export const LIMITY_NIEPOKAZYWANE = new Set<string>(['klatek_na_minute']);

export function kluczeLimitowZeZdaniem(): string[] {
  return Object.keys(ZDANIA_LIMITOW);
}

/** Polska odmiana liczebnikowa: 1 lokalizacja, 2–4 lokalizacje, 5+ lokalizacji. */
export function odmiana(n: number, jeden: string, kilka: string, wiele: string): string {
  const a = Math.abs(Math.trunc(n));
  if (a === 1) return jeden;
  const r10 = a % 10;
  const r100 = a % 100;
  if (r10 >= 2 && r10 <= 4 && !(r100 >= 12 && r100 <= 14)) return kilka;
  return wiele;
}

/** Limity cennika jako zdania dla klienta; klucze bez zdania i puste zdania odpadają. */
export function zdaniaLimitow(limity: Record<string, unknown> | null | undefined): string[] {
  if (!limity) return [];
  const wynik: string[] = [];
  for (const [klucz, wartosc] of Object.entries(limity)) {
    if (LIMITY_NIEPOKAZYWANE.has(klucz)) continue;
    const zdanie = ZDANIA_LIMITOW[klucz];
    if (!zdanie) continue;
    const n = typeof wartosc === 'number' ? wartosc : Number(wartosc);
    if (!Number.isFinite(n)) continue;
    const tekst = zdanie(n);
    if (tekst) wynik.push(tekst);
  }
  return wynik;
}

/** Nazwa pozycji, którą klient rozpoznaje — z karty, a gdy jej nie ma, z cennika. */
export function nazwaDlaKlienta(pozycja: PozycjaOfertyDto): string {
  return pozycja.karta?.nazwa_klienta?.trim() || pozycja.nazwa;
}

/**
 * „Co dostajesz w tym pakiecie” — jedna lista bez dubli: wariant karty,
 * ogólne funkcje karty, na końcu limity jako zdania.
 *
 * KARTA WYGRYWA NAD CENNIKIEM i to jest decyzja, nie kolejność przypadkowa.
 * `funkcje_pakietu` pochodzą z `plan_prices.features_pl` /
 * `module_pricing_tiers.features_pl` — to lista dla PARTNERA w panelu wyceny,
 * pisana skrótami. Do 03.09.2026 szła na ekran klienta pierwsza i plan Pro
 * pokazywał w ofercie OF-2026-0011: „10 miejsc współbieżnych (floating)”,
 * „Nexus pełny”, „3 urządzenia + 1 TV”, „2000 iTokenów/mc” — czyli dokładnie
 * słownik zakazany wobec klienta (`09-STRATEGIA-BIZNES/08-PAKIETY-I-OFERTY.md`
 * §1.2 i §7.3), którego pilnuje bramka na seedzie kart. Wchodził bokiem.
 *
 * Cennik zostaje jako droga zapasowa dla pozycji, która nie ma jeszcze karty
 * (edytor kart oznacza taką pozycję jako niekompletną) — lepiej pokazać skrót
 * z cennika niż samą nazwę i cenę. Kryterium jest OBECNOŚĆ FUNKCJI W KARCIE,
 * a nie jej źródło: karta zbudowana z cennika (`zrodlo: 'cennik'`) niesie te
 * same zdania, więc rozstrzyganie po `zrodlo` dawałoby ten sam tekst dwa razy.
 */
export function listaFunkcji(pozycja: PozycjaOfertyDto): string[] {
  const widziane = new Set<string>();
  const wynik: string[] = [];
  const dodaj = (f: string | null | undefined) => {
    const t = (f ?? '').trim();
    if (!t) return;
    const klucz = t.toLocaleLowerCase('pl-PL');
    if (widziane.has(klucz)) return;
    widziane.add(klucz);
    wynik.push(t);
  };
  const zKarty = [
    ...(pozycja.karta?.wariant?.funkcje ?? []),
    ...(pozycja.karta?.funkcje ?? []),
  ].filter((f) => (f ?? '').trim());
  if (zKarty.length) for (const f of zKarty) dodaj(f);
  else for (const f of pozycja.funkcje_pakietu ?? []) dodaj(f);
  for (const f of zdaniaLimitow(pozycja.limity_pakietu)) dodaj(f);
  return wynik;
}

/** Czy pozycja ma cokolwiek do rozwinięcia poza nazwą i ceną. */
export function maSzczegoly(pozycja: PozycjaOfertyDto): boolean {
  const k = pozycja.karta;
  if (k) {
    if (k.naglowek || k.opis || k.dla_kogo) return true;
    if (
      k.korzysci.length ||
      k.funkcje.length ||
      k.jak_dziala.length ||
      k.faq.length ||
      k.media.length
    )
      return true;
    if (k.wariant?.naglowek || k.wariant?.funkcje?.length) return true;
  }
  return listaFunkcji(pozycja).length > 0;
}

/**
 * „Co zyskujesz w skrócie” nad kwotami: po jednej korzyści z każdej pozycji
 * mającej kartę, plan pierwszy, bez powtórzeń, najwyżej cztery.
 */
export function korzysciWSkrocie(pozycje: PozycjaOfertyDto[], maks = 4): Korzysc[] {
  const posortowane = [...pozycje].sort((a, b) => waga(a.typ) - waga(b.typ));
  const widziane = new Set<string>();
  const wynik: Korzysc[] = [];
  for (const p of posortowane) {
    const pierwsza = p.karta?.korzysci?.find((k) => k.tytul?.trim());
    if (!pierwsza) continue;
    const klucz = pierwsza.tytul.trim().toLocaleLowerCase('pl-PL');
    if (widziane.has(klucz)) continue;
    widziane.add(klucz);
    wynik.push(pierwsza);
    if (wynik.length >= maks) break;
  }
  return wynik;
}

function waga(typ: TypPozycji): number {
  return typ === 'plan' ? 0 : typ === 'modul' ? 1 : 2;
}

/** Która pozycja ma być otwarta na wejściu: plan, i tylko gdy ma kartę z katalogu. */
export function indeksOtwartyNaStart(pozycje: PozycjaOfertyDto[]): number | null {
  const i = pozycje.findIndex((p) => p.typ === 'plan' && p.karta?.zrodlo === 'katalog');
  return i >= 0 ? i : null;
}

// ─── Media ──────────────────────────────────────────────────────────────────

const SCIEZKA =
  /^(plan|modul|licencja)\/[a-z0-9_:.-]+\/[0-9a-f-]{36}\.(png|jpe?g|webp|gif|mp4|webm)$/i;
const ID_YOUTUBE = /^[A-Za-z0-9_-]{11}$/;
const ID_VIMEO = /^\d{6,}$/;

export const BUCKET_KATALOG_MEDIA = 'katalog-media';

/** Publiczny adres pliku z bucketu; `bazaProjektu` to `VITE_SUPABASE_URL`. */
export function adresPliku(
  sciezka: string | null | undefined,
  bazaProjektu: string
): string | null {
  const s = (sciezka ?? '').trim();
  if (!SCIEZKA.test(s)) return null;
  const baza = bazaProjektu.replace(/\/+$/, '');
  if (!/^https?:\/\//i.test(baza)) return null;
  return `${baza}/storage/v1/object/public/${BUCKET_KATALOG_MEDIA}/${s}`;
}

export interface OsadzenieWideoInfo {
  dostawca: DostawcaWideo;
  /** Adres iframe (YouTube/Vimeo) albo pliku (`plik`). */
  src: string;
}

/**
 * Adres odtwarzania. YouTube idzie przez `youtube-nocookie.com`, Vimeo
 * z `dnt=1` — klient czyta ofertę bez zgody na cudze ciasteczka, więc ramka
 * ma nie zostawiać śladu, dopóki sam nie kliknie „odtwórz”.
 */
export function rozpoznajWideo(medium: Medium, bazaProjektu: string): OsadzenieWideoInfo | null {
  if (medium.rodzaj !== 'wideo') return null;
  if (medium.dostawca === 'youtube') {
    const id = (medium.id_wideo ?? '').trim();
    if (!ID_YOUTUBE.test(id)) return null;
    return {
      dostawca: 'youtube',
      src: `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`,
    };
  }
  if (medium.dostawca === 'vimeo') {
    const id = (medium.id_wideo ?? '').trim();
    if (!ID_VIMEO.test(id)) return null;
    return { dostawca: 'vimeo', src: `https://player.vimeo.com/video/${id}?autoplay=1&dnt=1` };
  }
  const src = adresPliku(medium.sciezka, bazaProjektu);
  return src && /\.(mp4|webm)$/i.test(src) ? { dostawca: 'plik', src } : null;
}

/** Miniatura do pokazania w galerii: z bucketu, a dla YouTube z jego CDN. */
export function adresMiniatury(medium: Medium, bazaProjektu: string): string | null {
  const wlasna = adresPliku(medium.miniatura, bazaProjektu);
  if (wlasna) return wlasna;
  if (medium.rodzaj === 'obraz') return adresPliku(medium.sciezka, bazaProjektu);
  if (medium.dostawca === 'youtube' && ID_YOUTUBE.test((medium.id_wideo ?? '').trim())) {
    return `https://i.ytimg.com/vi/${medium.id_wideo!.trim()}/hqdefault.jpg`;
  }
  return null;
}

/** Media, które strona umie pokazać: poprawna ścieżka obrazu albo rozpoznane wideo. */
export function mediaDoPokazania(
  media: Medium[] | null | undefined,
  bazaProjektu: string
): Medium[] {
  return (media ?? []).filter((m) => {
    if (!m || (m.rodzaj !== 'obraz' && m.rodzaj !== 'wideo')) return false;
    if (m.rodzaj === 'obraz') return adresPliku(m.sciezka, bazaProjektu) !== null;
    return rozpoznajWideo(m, bazaProjektu) !== null;
  });
}

/** Rozpoznanie identyfikatora z wklejonego adresu — dla edytora, nie dla strony klienta. */
export function idWideoZAdresu(
  adres: string
): { dostawca: 'youtube' | 'vimeo'; id: string } | null {
  const u = adres.trim();
  const yt = u.match(
    /(?:youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
  );
  if (yt) return { dostawca: 'youtube', id: yt[1] };
  if (ID_YOUTUBE.test(u)) return { dostawca: 'youtube', id: u };
  const vm = u.match(/vimeo\.com\/(?:video\/)?(\d{6,})/);
  if (vm) return { dostawca: 'vimeo', id: vm[1] };
  if (ID_VIMEO.test(u)) return { dostawca: 'vimeo', id: u };
  return null;
}

export function formatujKwote(wartosc: number | string, waluta: string): string {
  const n = typeof wartosc === 'number' ? wartosc : Number.parseFloat(String(wartosc)) || 0;
  return new Intl.NumberFormat('pl-PL', {
    style: 'currency',
    currency: waluta,
    maximumFractionDigits: 2,
  }).format(n);
}

export function formatujDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pl-PL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}
